import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Play,
  Youtube,
  Instagram,
  Music2,
  ArrowUpRight,
  Mail,
  Headphones,
  Menu,
  X,
} from "lucide-react";

const LOGO_MARK =
  "https://customer-assets.emergentagent.com/job_07c6442c-af07-4ca1-ad88-172c3ff15047/artifacts/pz20354e_james-kash-logo-mark-black.jpg";

const BOOKING_EMAIL = "musicbyjameskash@gmail.com";

const SPOTIFY_ARTIST = "https://open.spotify.com/artist/5CgCnWwmChEM56OckKURX5";
const YOUTUBE_CHANNEL = "https://www.youtube.com/@musicbyjameskash";
const APPLE_SEARCH = "https://music.apple.com/us/artist/james-kash/1440131741";
const SOUNDCLOUD_SEARCH = "https://on.soundcloud.com/rrcDUcXwmibwV5sCHi";
const INSTAGRAM_URL = "https://www.instagram.com/musicbyjameskash";

const cover = (hash) => `https://i.scdn.co/image/ab67616d0000b273${hash}`;

/* ---------- DATA ---------- */
const FEATURED = {
  title: "Burn",
  type: "Single",
  year: "2026",
  description:
    "The newest single from James Kash — dystopian, hard-hitting melodic future bass track.",
  cover: cover("29a076c392e7b0c80a4a732c"),
  links: {
    spotify: "https://open.spotify.com/album/5Ja69CYAPGUtJ5irpdWo6d",
    apple: APPLE_SEARCH,
    youtube: YOUTUBE_CHANNEL,
    soundcloud: "https://soundcloud.com/jameskash/burn-1",
  },
};

const RELEASES = [
  {
    title: "Burn",
    year: "2026",
    type: "Single",
    cover: cover("29a076c392e7b0c80a4a732c"),
    href: "https://open.spotify.com/album/5Ja69CYAPGUtJ5irpdWo6d",
  },
  {
    title: "bombs away",
    year: "2025",
    type: "Single",
    cover: cover("bd63208700d7909165a5a7bd"),
    href: "https://open.spotify.com/album/49Fg8D9eWid2hhcUfb246C",
  },
  {
    title: "Postman",
    year: "2025",
    type: "Single",
    cover: cover("91763fa02672c4f52571136a"),
    href: "https://open.spotify.com/album/0ox4N85uzc7O8bAbepi8As",
  },
  {
    title: "fly free",
    year: "2025",
    type: "Single",
    cover: cover("f252389abff8b7d0316777ba"),
    href: "https://open.spotify.com/album/2sB9xnFGVvGoesH3kL3PN5",
  },
  {
    title: "underwater sun",
    year: "2025",
    type: "Single",
    cover: cover("b088ab49f799afa1df5467c2"),
    href: "https://open.spotify.com/album/75nlkrmpd9ysb9naM8EZLu",
  },
  {
    title: "The Garden",
    year: "2024",
    type: "Single",
    cover: cover("2fc7ef160b155a6ad5802abc"),
    href: "https://open.spotify.com/album/7pEdvrD0HR15RmvfeEtyK0",
  },
  {
    title: "Reverence",
    year: "2023",
    type: "Single",
    cover: cover("222f5648c22611cdbcd15b46"),
    href: "https://open.spotify.com/album/4TTl8pppVe7flYfbDUjXKM",
  },
  {
    title: "Live for Now",
    year: "2022",
    type: "Single",
    cover: cover("5413e628e5d96cce870952cb"),
    href: "https://open.spotify.com/album/3YLb10wvQLcVaC92eSRu7a",
  },
  {
    title: "This Is Life",
    year: "2022",
    type: "Single",
    cover: cover("a6b8d7c5ea7993c3d4e5bf17"),
    href: "https://open.spotify.com/album/2cS2WhrZsevUXiDUZ3G0fM",
  },
  {
    title: "Fell Out of Love",
    year: "2019",
    type: "Single",
    cover: cover("2be40e406f3c6a03fdb80363"),
    href: "https://open.spotify.com/album/7tPF7FYWmiOtIhcViwhR89",
  },
];

const VIDEOS = [
  {
    id: "zxGRXvwGNyQ",
    title: "Reverence — Official Music Video",
    tag: "Music Video",
  },
  {
    id: "VbKtq2u6JhY",
    title: "Postman — Official Lyric Video",
    tag: "Lyric Video",
  },
  {
    id: "zbALgMZmE9E",
    title: "The Garden — Official Visualizer",
    tag: "Visualizer",
  },
];

const SOCIALS = [
  { name: "Spotify", icon: Headphones, href: SPOTIFY_ARTIST },
  { name: "Apple Music", icon: Music2, href: APPLE_SEARCH },
  { name: "YouTube", icon: Youtube, href: YOUTUBE_CHANNEL },
  { name: "SoundCloud", icon: Music2, href: SOUNDCLOUD_SEARCH },
  { name: "Instagram", icon: Instagram, href: INSTAGRAM_URL },
];

/* ---------- COMPONENTS ---------- */

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#music", label: "Music" },
    { href: "#about", label: "About" },
    { href: "#discography", label: "Releases" },
    { href: "#videos", label: "Visuals" },
    { href: "#contact", label: "Booking" },
  ];

  return (
    <nav
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#05050A]/80 backdrop-blur-2xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center justify-between">
        <a
          href="#hero"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span
            className="k-mask block bg-white w-7 h-7"
            aria-hidden="true"
          />
          <span className="font-display font-bold text-lg tracking-tight">
            JAMES KASH
          </span>
        </a>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-medium text-zinc-400 hover:text-white transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </div>

        <a
          href={SPOTIFY_ARTIST}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-listen-btn"
          className="hidden md:inline-flex items-center gap-2 bg-white text-black text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-zinc-200 active:scale-95 transition-all"
        >
          Listen <Play size={14} className="fill-black" />
        </a>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#05050A]/95 backdrop-blur-xl border-t border-white/5"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="text-base font-medium text-zinc-300 hover:text-white"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden flex items-center justify-center grain"
    >
      {/* Aurora glows */}
      <div
        className="aurora"
        style={{
          background: "#3B82F6",
          width: 600,
          height: 600,
          top: "-10%",
          left: "-10%",
          opacity: 0.35,
        }}
      />
      <div
        className="aurora"
        style={{
          background: "#8B5CF6",
          width: 700,
          height: 700,
          bottom: "-20%",
          right: "-15%",
          opacity: 0.3,
        }}
      />
      <div
        className="aurora"
        style={{
          background: "#1e3a8a",
          width: 500,
          height: 500,
          top: "30%",
          left: "40%",
          opacity: 0.4,
        }}
      />

      {/* Giant K motif background */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        aria-hidden="true"
      >
        <div
          className="k-mask bg-white/[0.04] w-[90vmin] h-[90vmin]"
          style={{ transform: "rotate(-8deg)" }}
        />
      </div>

      {/* Faint grid */}
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />

      <div className="relative z-10 w-full min-w-0 max-w-6xl mx-auto px-6 sm:px-8 text-center">
        <h1
          data-testid="hero-title"
          className="hero-title reveal reveal-2 text-[clamp(3rem,13vw,9.5rem)]"
        >
          JAMES
          <br />
          KASH
        </h1>

        <p
          data-testid="hero-subtitle"
          className="reveal reveal-3 mt-10 max-w-xl mx-auto text-base sm:text-lg text-zinc-400 leading-relaxed"
        >
          Melodic electronic, made in Chicago.
        </p>

        <div className="reveal reveal-4 mt-12 flex flex-col items-center gap-5">
          <a
            href={SPOTIFY_ARTIST}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-listen-btn"
            className="group inline-flex items-center gap-3 bg-white text-black font-semibold px-8 py-4 rounded-full hover:bg-zinc-200 active:scale-95 transition-all shadow-[0_0_50px_-10px_rgba(255,255,255,0.4)]"
          >
            <Play size={16} className="fill-black" />
            Listen on Spotify
          </a>
          <a
            href={YOUTUBE_CHANNEL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-youtube-btn"
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-400 hover:text-white transition-colors"
          >
            <Youtube size={14} />
            Watch on YouTube
          </a>
        </div>

        {/* Waveform */}
        <div className="mt-20 overflow-hidden max-w-full">
          <div
            className="reveal reveal-4 flex items-end justify-center gap-1.5 h-12 opacity-50"
            aria-hidden="true"
          >
            {Array.from({ length: 48 }).map((_, i) => (
              <span
                key={i}
                className="wave-bar w-[3px] rounded-full bg-gradient-to-t from-blue-500/60 to-violet-300/80 shrink-0"
                style={{
                  height: `${20 + Math.abs(Math.sin(i * 0.6)) * 80}%`,
                  animationDelay: `${i * 0.04}s`,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Scroll hint removed for cleaner hero */}
    </section>
  );
};

const FeaturedRelease = () => {
  return (
    <section
      id="music"
      data-testid="featured-section"
      className="relative py-28 sm:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.3em] text-blue-400 mb-5">
              Latest
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
              Burn.
            </h2>
          </div>
          <a
            href="#discography"
            data-testid="featured-all-link"
            className="hidden sm:inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            All releases <ArrowUpRight size={16} />
          </a>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Cover + Spotify Embed Player */}
          <div className="relative group">
            <div className="absolute -inset-4 bg-gradient-to-br from-blue-600/40 via-violet-600/30 to-transparent blur-3xl opacity-60 group-hover:opacity-90 transition-opacity" />
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-[#0D0E15]">
              <div className="relative aspect-square">
                <img
                  src={FEATURED.cover}
                  alt={FEATURED.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              </div>
              <iframe
                data-testid="featured-spotify-player"
                src="https://open.spotify.com/embed/track/3bQmPP4NKB88fiAPPzyxze?utm_source=generator&theme=0"
                width="100%"
                height="80"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
                loading="lazy"
                title="Burn — Spotify preview"
                className="block w-full border-0"
                style={{ colorScheme: "normal" }}
              />
            </div>
            <p className="mt-5 text-xs uppercase tracking-[0.25em] text-zinc-600 text-center">
              30s preview · no account needed
            </p>
          </div>

          {/* Info */}
          <div>
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">
              {FEATURED.type} · {FEATURED.year}
            </span>
            <h3
              data-testid="featured-title"
              className="font-display text-5xl sm:text-6xl font-bold tracking-tight mb-6"
            >
              {FEATURED.title}
            </h3>
            <p className="text-zinc-400 text-base sm:text-lg leading-relaxed mb-10 max-w-lg">
              {FEATURED.description}
            </p>

            <div className="grid grid-cols-2 gap-3 max-w-md">
              <StreamBtn
                href={FEATURED.links.spotify}
                label="Spotify"
                testid="stream-spotify"
              />
              <StreamBtn
                href={FEATURED.links.apple}
                label="Apple Music"
                testid="stream-apple"
              />
              <StreamBtn
                href={FEATURED.links.youtube}
                label="YouTube"
                testid="stream-youtube"
              />
              <StreamBtn
                href={FEATURED.links.soundcloud}
                label="SoundCloud"
                testid="stream-soundcloud"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StreamBtn = ({ href, label, testid }) => (
  <a
    href={href}
    data-testid={testid}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex items-center justify-between px-5 py-3.5 rounded-full border border-white/10 bg-white/[0.02] hover:bg-white/10 hover:border-white/30 transition-all"
  >
    <span className="text-sm font-medium">{label}</span>
    <ArrowUpRight
      size={16}
      className="text-zinc-500 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
    />
  </a>
);

const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-28 sm:py-40 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
        <div className="lg:col-span-7">
          <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.3em] text-blue-400 mb-6">
            Artist
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-10">
            Made for the moments that move you.
          </h2>
          <p className="text-zinc-400 text-base sm:text-lg leading-relaxed max-w-xl mb-6">
            James Kash is a Chicago producer working in melodic electronic
            music — cinematic, charged, and built for release. Late nights,
            long drives, festival fields. Music that lives somewhere between
            the headphones and the front row.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            {["Melodic Bass", "Future Bass", "Cinematic EDM"].map((g) => (
              <span
                key={g}
                className="text-xs uppercase tracking-[0.2em] text-zinc-300 px-4 py-2 rounded-full border border-white/10 bg-white/[0.02]"
              >
                {g}
              </span>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="absolute -inset-6 bg-gradient-to-br from-blue-600/30 to-violet-600/20 blur-3xl rounded-full" />
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/10 bg-black">
            <img
              src="https://customer-assets.emergentagent.com/job_kash-beats/artifacts/wg3bb6co_7.jpg"
              alt="James Kash"
              className="w-full h-full object-cover"
              style={{
                filter:
                  "grayscale(15%) brightness(0.92) contrast(1.06) saturate(0.95)",
                objectPosition: "center 30%",
              }}
            />
            {/* Brand color wash */}
            <div
              className="absolute inset-0 pointer-events-none mix-blend-color"
              style={{
                background:
                  "linear-gradient(160deg, rgba(59,130,246,0.18) 0%, rgba(20,28,60,0.12) 50%, rgba(139,92,246,0.18) 100%)",
              }}
            />
            {/* Vignette + bottom fade */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/15 to-transparent" />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.3) 100%)",
              }}
            />
            <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between z-10">
              <span className="text-xs uppercase tracking-[0.3em] text-white/80">
                Based in Chicago
              </span>
              <span
                className="k-mask bg-white w-6 h-6"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Discography = () => {
  return (
    <section
      id="discography"
      data-testid="discography-section"
      className="relative py-28 sm:py-40 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-4">
          <div>
            <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.3em] text-blue-400 mb-5">
              Releases
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
              Catalog.
            </h2>
          </div>
          <p className="text-zinc-500 text-sm max-w-xs">
            Singles and EPs from 2019 — present.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
          {RELEASES.map((r, i) => (
            <ReleaseCard key={i} release={r} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ReleaseCard = ({ release, idx }) => (
  <a
    href={release.href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`release-card-${idx}`}
    className="group relative aspect-square rounded-2xl overflow-hidden border border-white/5 bg-[#0D0E15] hover:border-white/20 transition-all duration-500 hover:-translate-y-1 block"
  >
    <img
      src={release.cover}
      alt={release.title}
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
    />
    {/* Unified darkening overlay (top + bottom) — keeps cards visually consistent across varied artwork */}
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/0" />
    <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-transparent" />

    {/* Top eyebrow */}
    <div className="absolute top-5 left-5 right-5 flex items-center justify-between text-xs uppercase tracking-[0.25em] text-white/70">
      <span>{release.year}</span>
      <span>{release.type}</span>
    </div>

    {/* Bottom title + arrow */}
    <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between">
      <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-white leading-tight">
        {release.title}
      </h3>
      <span className="opacity-60 group-hover:opacity-100 transition-opacity">
        <ArrowUpRight
          size={18}
          className="text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
        />
      </span>
    </div>

    {/* Hover play overlay */}
    <span
      data-testid={`release-play-${idx}`}
      className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
      aria-label={`Play ${release.title}`}
    >
      <span className="w-14 h-14 rounded-full bg-white text-black flex items-center justify-center scale-90 group-hover:scale-100 transition-transform shadow-[0_0_30px_rgba(255,255,255,0.35)]">
        <Play size={18} className="fill-black ml-0.5" />
      </span>
    </span>
  </a>
);

const Videos = () => {
  const [active, setActive] = useState(0);
  return (
    <section
      id="videos"
      data-testid="videos-section"
      className="relative py-28 sm:py-40 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="mb-16">
          <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.3em] text-blue-400 mb-5">
            Visuals
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            On screen.
          </h2>
        </div>

        <div className="relative">
          <div className="absolute -inset-8 bg-gradient-to-br from-blue-600/25 via-violet-600/15 to-transparent blur-3xl pointer-events-none" />
          <div className="relative aspect-video rounded-3xl overflow-hidden border border-white/10 bg-black">
            <iframe
              key={VIDEOS[active].id}
              data-testid="video-player"
              src={`https://www.youtube.com/embed/${VIDEOS[active].id}?rel=0`}
              title={VIDEOS[active].title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          {VIDEOS.map((v, i) => (
            <button
              key={v.id}
              data-testid={`video-thumb-${i}`}
              onClick={() => setActive(i)}
              className={`group text-left p-5 rounded-2xl border transition-all ${
                active === i
                  ? "border-white/30 bg-white/[0.05]"
                  : "border-white/5 bg-[#0D0E15] hover:border-white/15"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.25em] text-blue-400">
                  {v.tag}
                </span>
                <Play
                  size={14}
                  className={`${
                    active === i ? "text-white" : "text-zinc-500"
                  } group-hover:text-white transition-colors`}
                />
              </div>
              <h4 className="font-display font-semibold text-base leading-snug">
                {v.title}
              </h4>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill out all fields before sending.");
      return;
    }
    const subject = encodeURIComponent(`Booking inquiry — ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    );
    window.location.href = `mailto:${BOOKING_EMAIL}?subject=${subject}&body=${body}`;
    toast.success("Opening your email client…");
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 sm:py-40 border-t border-white/5 overflow-hidden"
    >
      <div
        className="aurora"
        style={{
          background: "#3B82F6",
          width: 600,
          height: 600,
          top: "20%",
          left: "-15%",
          opacity: 0.2,
        }}
      />

      <div className="relative max-w-3xl mx-auto px-6 sm:px-8 text-center">
        <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.3em] text-blue-400 mb-5">
          Contact
        </p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
          Get in touch.
        </h2>
        <p className="text-zinc-400 text-base sm:text-lg leading-relaxed mb-12 max-w-xl mx-auto">
          Booking, sync, and label inquiries.
        </p>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="grid gap-4 text-left"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              type="text"
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="contact-name"
              className="bg-white/[0.03] border-white/10 h-12 rounded-xl placeholder:text-zinc-600 focus-visible:ring-blue-500"
            />
            <Input
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="contact-email"
              className="bg-white/[0.03] border-white/10 h-12 rounded-xl placeholder:text-zinc-600 focus-visible:ring-blue-500"
            />
          </div>
          <Textarea
            rows={5}
            placeholder="Tell us about your project, venue, or release…"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="contact-message"
            className="bg-white/[0.03] border-white/10 rounded-xl placeholder:text-zinc-600 focus-visible:ring-blue-500 resize-none"
          />
          <Button
            type="submit"
            data-testid="contact-submit"
            className="mt-2 h-12 rounded-full bg-white text-black hover:bg-zinc-200 font-semibold text-base"
          >
            Send Inquiry
            <ArrowUpRight size={16} className="ml-1" />
          </Button>
        </form>

        <div className="mt-14 pt-10 border-t border-white/5">
          <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
            Or email directly
          </p>
          <a
            href={`mailto:${BOOKING_EMAIL}`}
            data-testid="booking-email-link"
            className="inline-flex items-center gap-3 font-display font-bold tracking-tight hover:text-blue-300 transition-colors whitespace-nowrap text-[clamp(1rem,4.2vw,2.25rem)]"
          >
            <Mail className="shrink-0 w-[1em] h-[1em]" />
            {BOOKING_EMAIL}
          </a>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer
      data-testid="footer"
      className="relative border-t border-white/5 py-16 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col lg:flex-row justify-between gap-12">
          <div className="max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <span
                className="k-mask block bg-white w-8 h-8"
                aria-hidden="true"
              />
              <span className="font-display font-bold text-xl tracking-tight">
                JAMES KASH
              </span>
            </div>
            <p className="text-zinc-500 text-sm leading-relaxed">
              Melodic electronic, made in Chicago.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-10 lg:gap-16">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">
                Explore
              </p>
              <ul className="space-y-2.5 text-sm">
                {[
                  ["#music", "Music"],
                  ["#about", "About"],
                  ["#discography", "Releases"],
                  ["#videos", "Visuals"],
                ].map(([h, l]) => (
                  <li key={h}>
                    <a
                      href={h}
                      className="text-zinc-300 hover:text-white transition-colors"
                    >
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">
                Listen
              </p>
              <ul className="space-y-2.5 text-sm">
                {SOCIALS.map((s) => (
                  <li key={s.name}>
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`social-${s.name.toLowerCase().replace(/\s/g, "-")}`}
                      className="inline-flex items-center gap-2 text-zinc-300 hover:text-white transition-colors"
                    >
                      <s.icon size={14} /> {s.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">
                Contact
              </p>
              <a
                href={`mailto:${BOOKING_EMAIL}`}
                data-testid="footer-email"
                className="text-zinc-300 hover:text-white transition-colors text-sm break-words [overflow-wrap:anywhere]"
              >
                {BOOKING_EMAIL}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-zinc-600">
          <p>© {new Date().getFullYear()} James Kash. All rights reserved.</p>
          <p className="uppercase tracking-[0.3em]">Melodic Electronic Music</p>
        </div>
      </div>
    </footer>
  );
};

const Home = () => {
  return (
    <div className="relative min-h-screen bg-[#05050A] text-white font-body">
      <Nav />
      <Hero />
      <FeaturedRelease />
      <About />
      <Discography />
      <Videos />
      <Contact />
      <Footer />
      <Toaster position="bottom-center" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
